import React, { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import apiClient from '../../api/client';

export default function SewerInstructions() {
  const [showScanModal, setShowScanModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [partNumber, setPartNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (showScanModal && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showScanModal]);

  // Watch for part number changes and auto-submit when 5+ characters
  useEffect(() => {
    if (partNumber.length >= 5) {
      // Replace [ with - as per original logic
      const sanitizedPartNumber = partNumber.replace(/\[/g, '-');
      handleSearch(sanitizedPartNumber);
    }
  }, [partNumber]);

  const handleSearch = async (searchText: string) => {
    setIsLoading(true);
    setShowScanModal(false);

    try {
      // Check if file exists
      const checkResponse: any = await apiClient.get(`/sewing-instructions/check/${searchText}`);

      if (checkResponse.data?.exists) {
        // Open PDF directly from backend
        const token = localStorage.getItem('token');
        const fileUrl = `/api/v1/sewing-instructions/file/${searchText}`;

        // Use window.location for better mobile compatibility (especially Safari on iPhone)
        // This avoids popup blocker issues
        window.location.href = `${fileUrl}?token=${token}`;
        setPartNumber('');
      } else {
        setShowErrorModal(true);
        setPartNumber('');
      }
    } catch (error) {
      setShowErrorModal(true);
      setPartNumber('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="w-full">
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Sewing Instructions
            </h1>
          </div>
          <div className="p-8 text-center">
            <button
              onClick={() => setShowScanModal(true)}
              className="px-6 py-3 bg-blue-600 text-white text-lg font-medium rounded hover:bg-blue-700"
            >
              GET SEWING INSTRUCTION
            </button>
          </div>
        </div>
      </div>

      {/* Scan Modal */}
      {showScanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Scan your item
              </h3>
              <button
                onClick={() => {
                  setShowScanModal(false);
                  setPartNumber('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4 text-center leading-relaxed">
                Please scan your item<br />
                Por favor, escanear la copia impresa
              </p>
              <input
                ref={inputRef}
                type="text"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg"
                placeholder="Scan part number..."
              />
              {isLoading && (
                <div className="mt-4 text-center text-gray-600 dark:text-gray-400">
                  Loading...
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowScanModal(false);
                  setPartNumber('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Item not accepted
              </h3>
              <button
                onClick={() => setShowErrorModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-4 text-center">
                <p className="text-lg text-red-800 dark:text-red-300 leading-relaxed">
                  Instructions not found<br />
                  instrucciones no encontradas
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
